/* ==========================================================================
   MiaouLab - offres par quantite (lots) et choix de variante par unite.

   Trois roles :

   1. Deposer la quantite choisie dans le formulaire d'achat de Dawn. Le
      template de fiche n'a pas de selecteur de quantite : sans cela le
      formulaire partirait toujours a 1. Aucune modification de
      buy-buttons.liquid n'est necessaire.

   2. Construire un selecteur par unite supplementaire des qu'un lot est
      retenu. L'unite 1 reste pilotee par le selecteur de Dawn, qui commande
      aussi le prix, la galerie et l'URL : on n'en affiche qu'un rappel.
      Une unite que l'acheteur n'a pas touchee suit l'unite 1 ; des qu'il la
      modifie, son choix est conserve.

   3. Envoyer le panier. Pour un lot, les unites peuvent viser des variantes
      differentes : /cart/add.js doit alors recevoir plusieurs lignes, ce que
      le formulaire de Dawn ne sait pas faire. On intercepte donc l'envoi en
      phase de capture, donc avant Dawn, et on refait TOUT ce que fait
      product-form.js, y compris ce qui ne se voit pas :

        - retirer is-empty sur <cart-drawer>. renderContents ne nettoie que
          .drawer__inner, or dans ce theme la classe est portee par
          <cart-drawer> lui-meme, et #CartDrawer - le seul noeud remplace -
          est a l'interieur. Sans ce retrait, la regle
          « cart-drawer.is-empty .drawer__inner » remplace la mise en page
          flex par une grille centree et masque l'en-tete : les lignes du
          panier disparaissent jusqu'au rechargement de la page.

        - memoriser l'element actif, pour que le focus revienne au bouton
          quand le tiroir se ferme.

        - emettre CartLinesUpdateEvent (API StandardEvents de Shopify), qui
          alimente le suivi « ajout au panier » des pixels. Sans lui un lot
          ne serait pas trace alors qu'une unite seule l'est.

        - publier cartUpdate avant le rendu, et cartError en cas d'echec.

   Pour un lot de 1, on ne touche a rien : Dawn fait son travail habituel.

   Deux pieges evites pour la resynchronisation au changement de variante :

   - ne pas surveiller .price : Dawn remplace le innerHTML du conteneur
     #price-<section>, donc le noeud observe est detache au premier
     changement et l'observateur ne se declenche plus jamais. On passe par
     le pub/sub de Dawn, et le repli surveille le conteneur, qui reste.

   - ne pas differer le travail dans requestAnimationFrame : ses rappels ne
     s'executent pas dans un document non rendu (onglet en arriere-plan),
     et la mise a jour serait alors perdue. Dawn publie variantChange APRES
     avoir mis a jour le champ id du formulaire : l'appel direct est donc
     a la fois correct et sans dependance a l'affichage.

   Aucune librairie.
   ========================================================================== */

if (!customElements.get('mia-lot')) {
  customElements.define(
    'mia-lot',
    class MiaLot extends HTMLElement {
      connectedCallback() {
        this.choix = Array.from(this.querySelectorAll('.mia-lot__input'));
        if (this.choix.length === 0) return;

        this.donnees = this.lireDonnees();
        this.zone = this.querySelector('[data-mia-lot-unites]');
        this.nom = this.dataset.nom || 'exemplaire';
        this.formulaire = this.trouverFormulaire();
        this.unites = [];
        this.focusApres = null;

        this.choix.forEach((c) => c.addEventListener('change', () => this.appliquer()));

        this.surEnvoi = (e) => this.gererEnvoi(e);
        document.addEventListener('submit', this.surEnvoi, true);

        this.ecouterVariante();
        this.appliquer();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.surEnvoi, true);
        if (this.desabonner) this.desabonner();
        if (this.observateur) this.observateur.disconnect();
      }

      ecouterVariante() {
        const rafraichir = () => this.appliquer();

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.desabonner = subscribe(PUB_SUB_EVENTS.variantChange, rafraichir);
          return;
        }

        const conteneur = document.querySelector('[id^="price-"]');
        if (conteneur) {
          this.observateur = new MutationObserver(rafraichir);
          this.observateur.observe(conteneur, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      }

      lireDonnees() {
        try {
          return JSON.parse(this.querySelector('[data-mia-lot-donnees]').textContent);
        } catch (e) {
          return { noms: [], variantes: [] };
        }
      }

      trouverFormulaire() {
        const p = this.closest('product-info') || document;
        return (
          p.querySelector('product-form form[data-type="add-to-cart-form"]') ||
          p.querySelector('product-form form') ||
          null
        );
      }

      trouverPanier() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      get quantite() {
        const r = this.choix.find((c) => c.checked);
        const n = r ? parseInt(r.value, 10) : 1;
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      /* ---------------- catalogue de variantes ---------------- */

      varianteParId(id) {
        return this.donnees.variantes.find((v) => String(v.id) === String(id)) || null;
      }

      optionsUnite1() {
        const champ = this.formulaire && this.formulaire.querySelector('[name="id"]');
        const v = champ ? this.varianteParId(champ.value) : null;
        if (v) return v.o.slice();
        const secours = this.donnees.variantes.find((x) => x.d) || this.donnees.variantes[0];
        return secours ? secours.o.slice() : [];
      }

      valeurs(k) {
        const vues = [];
        for (const v of this.donnees.variantes) {
          if (vues.indexOf(v.o[k]) === -1) vues.push(v.o[k]);
        }
        return vues;
      }

      possible(k, valeur, sel) {
        return this.donnees.variantes.some(
          (v) => v.d && v.o[k] === valeur && sel.every((s, j) => j === k || v.o[j] === s)
        );
      }

      varianteDe(sel) {
        return this.donnees.variantes.find((v) => v.o.every((x, j) => x === sel[j])) || null;
      }

      reparer(sel, kFige) {
        const v = this.varianteDe(sel);
        if (v && v.d) return sel;
        for (let k = 0; k < sel.length; k++) {
          if (k === kFige) continue;
          const ouvertes = this.valeurs(k).filter((x) => this.possible(k, x, sel));
          if (ouvertes.length > 0 && ouvertes.indexOf(sel[k]) === -1) sel[k] = ouvertes[0];
        }
        return sel;
      }

      /* ---------------- rendu ---------------- */

      appliquer() {
        this.ecrireQuantite();
        this.majBarreMobile();
        this.rendreUnites();
      }

      etiquette(i) {
        return this.nom.charAt(0).toUpperCase() + this.nom.slice(1) + ' ' + i;
      }

      rendreUnites() {
        if (!this.zone) return;
        const n = this.quantite;
        const noms = this.donnees.noms || [];

        if (n < 2 || noms.length === 0 || this.donnees.variantes.length === 0) {
          this.zone.hidden = true;
          this.zone.textContent = '';
          return;
        }

        const base = this.optionsUnite1();

        while (this.unites.length < n - 1) this.unites.push({ sel: base.slice(), touche: false });
        this.unites.length = n - 1;
        for (const u of this.unites) {
          if (!u.touche) u.sel = base.slice();
        }

        this.zone.hidden = false;
        this.zone.textContent = '';

        const titre = document.createElement('p');
        titre.className = 'mia-lot__unites-titre';
        titre.textContent = 'Composez votre lot';
        this.zone.appendChild(titre);

        const l1 = document.createElement('div');
        l1.className = 'mia-lot__unite';
        const t1 = document.createElement('div');
        t1.className = 'mia-lot__unite-tete';
        const n1 = document.createElement('span');
        n1.className = 'mia-lot__unite-num';
        n1.textContent = this.etiquette(1);
        const r1 = document.createElement('span');
        r1.className = 'mia-lot__unite-recap';
        r1.textContent = base.join(' · ');
        const a1 = document.createElement('span');
        a1.className = 'mia-lot__unite-aide';
        a1.textContent = 'choisi ci-dessus';
        t1.appendChild(n1);
        t1.appendChild(r1);
        l1.appendChild(t1);
        l1.appendChild(a1);
        this.zone.appendChild(l1);

        this.unites.forEach((u, indice) => {
          const rang = indice + 2;
          const ligne = document.createElement('div');
          ligne.className = 'mia-lot__unite';

          const tete = document.createElement('div');
          tete.className = 'mia-lot__unite-tete';
          const num = document.createElement('span');
          num.className = 'mia-lot__unite-num';
          num.textContent = this.etiquette(rang);
          tete.appendChild(num);
          ligne.appendChild(tete);

          const champs = document.createElement('div');
          champs.className = 'mia-lot__champs';
          noms.forEach((_, k) => champs.appendChild(this.creerSelect(k, u, rang, indice)));
          ligne.appendChild(champs);

          this.zone.appendChild(ligne);
        });

        if (this.focusApres) {
          const cible = this.zone.querySelector(
            '[data-unite="' + this.focusApres.u + '"][data-option="' + this.focusApres.k + '"]'
          );
          if (cible) cible.focus();
          this.focusApres = null;
        }
      }

      creerSelect(k, u, rang, indice) {
        const s = document.createElement('select');
        s.className = 'mia-lot__select';
        s.dataset.unite = String(indice);
        s.dataset.option = String(k);
        s.setAttribute('aria-label', this.donnees.noms[k] + ' du ' + this.nom + ' ' + rang);

        for (const val of this.valeurs(k)) {
          const o = document.createElement('option');
          o.value = val;
          if (this.possible(k, val, u.sel)) {
            o.textContent = val;
          } else {
            o.textContent = val + ' — indisponible';
            o.disabled = true;
          }
          if (u.sel[k] === val) o.selected = true;
          s.appendChild(o);
        }

        s.addEventListener('change', () => {
          u.sel[k] = s.value;
          u.touche = true;
          this.reparer(u.sel, k);
          this.focusApres = { u: indice, k: k };
          this.rendreUnites();
        });

        return s;
      }

      ecrireQuantite() {
        if (!this.formulaire || !this.formulaire.isConnected) {
          this.formulaire = this.trouverFormulaire();
        }
        if (!this.formulaire) return;
        let champ = this.formulaire.querySelector('[name="quantity"]');
        if (!champ) {
          champ = document.createElement('input');
          champ.type = 'hidden';
          champ.name = 'quantity';
          this.formulaire.appendChild(champ);
        }
        champ.value = String(this.quantite);
      }

      majBarreMobile() {
        const cible = document.querySelector('.mia-ps__prix');
        if (!cible) return;
        const r = this.choix.find((c) => c.checked);
        if (r && r.dataset.total) cible.textContent = r.dataset.total;
      }

      /* ---------------- envoi ---------------- */

      gererEnvoi(evenement) {
        if (!this.formulaire || !this.formulaire.isConnected) {
          this.formulaire = this.trouverFormulaire();
        }
        if (!this.formulaire || evenement.target !== this.formulaire) return;

        this.ecrireQuantite();
        const n = this.quantite;
        if (n < 2) return;

        evenement.preventDefault();
        evenement.stopImmediatePropagation();
        this.ajouter(n);
      }

      identifiants(n) {
        const ids = [];
        const champ = this.formulaire.querySelector('[name="id"]');
        if (champ && champ.value) ids.push(champ.value);
        for (const u of this.unites) {
          const v = this.varianteDe(u.sel);
          if (v && v.d) ids.push(String(v.id));
        }
        return ids.slice(0, n);
      }

      chargement(actif) {
        const btn = this.formulaire && this.formulaire.querySelector('[name="add"]');
        if (!btn) return;
        btn.classList.toggle('loading', actif);
        if (actif) {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('aria-busy');
        }
        const sp = btn.querySelector('.loading__spinner');
        if (sp) sp.classList.toggle('hidden', !actif);
      }

      erreur() {
        const pf = this.formulaire ? this.formulaire.closest('product-form') : null;
        const enveloppe = pf ? pf.querySelector('.product-form__error-message-wrapper') : null;
        const texte = pf ? pf.querySelector('.product-form__error-message') : null;
        if (texte) texte.textContent = 'Ajout impossible pour le moment.';
        if (enveloppe) enveloppe.removeAttribute('hidden');
      }

      masquerErreur() {
        const pf = this.formulaire ? this.formulaire.closest('product-form') : null;
        const enveloppe = pf ? pf.querySelector('.product-form__error-message-wrapper') : null;
        if (enveloppe) enveloppe.setAttribute('hidden', '');
      }

      /* Suivi : meme evenement que product-form.js, sinon un lot echapperait
         aux pixels alors qu'une unite seule est tracee. */
      evenementLignes(lignes) {
        const norme = window.StandardEvents || {};
        const Evenement = norme.CartLinesUpdateEvent;
        if (!Evenement) return null;
        const differe = Evenement.createPromise();
        const pf = this.formulaire ? this.formulaire.closest('product-form') : null;
        (pf || this).dispatchEvent(
          new Evenement({
            action: 'add',
            context: 'product',
            lines: lignes.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
            promise: differe.promise,
          })
        );
        return differe;
      }

      resoudreLignes(differe) {
        if (!differe) return;
        const Evenement = (window.StandardEvents || {}).CartLinesUpdateEvent;
        if (!Evenement) return;
        const attente =
          typeof CartItems !== 'undefined' && typeof CartItems.fetchCartData === 'function'
            ? CartItems.fetchCartData()
            : fetch(window.Shopify.routes.root + 'cart.json').then((r) => r.json());
        attente
          .then((panier) => {
            if (!panier || !panier.currency) {
              differe.reject(new Error('Devise absente de la reponse panier'));
              return;
            }
            differe.resolve({ cart: Evenement.createCartFromAjaxResponse(panier) });
          })
          .catch((e) => differe.reject(e));
      }

      async ajouter(n) {
        const ids = this.identifiants(n);
        if (ids.length < n) {
          this.erreur();
          return;
        }

        const compte = new Map();
        for (const id of ids) compte.set(id, (compte.get(id) || 0) + 1);
        const items = Array.from(compte, (paire) => ({
          id: Number(paire[0]),
          quantity: paire[1],
        }));

        const panier = this.trouverPanier();
        const sections = panier ? panier.getSectionsToRender().map((s) => s.id) : [];
        if (panier && typeof panier.setActiveElement === 'function') {
          panier.setActiveElement(document.activeElement);
        }

        const differe = this.evenementLignes(items);

        this.masquerErreur();
        this.chargement(true);

        try {
          const rep = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              items: items,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });
          const donnees = await rep.json();

          if (donnees.status) {
            this.erreur();
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'mia-p-lot',
                errors: donnees.errors || donnees.description,
                message: donnees.message,
              });
            }
            if (differe) differe.reject(new Error(donnees.description || donnees.message));
            return;
          }

          this.resoudreLignes(differe);

          if (!panier || typeof panier.renderContents !== 'function' || !donnees.sections) {
            window.location.href = window.Shopify.routes.root + 'cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mia-p-lot', cartData: donnees });
          }

          panier.renderContents(donnees);
        } catch (e) {
          this.erreur();
          if (differe) differe.reject(e);
        } finally {
          this.chargement(false);
          // LE correctif : renderContents ne nettoie que .drawer__inner, or
          // la classe est portee par <cart-drawer>, qui n'est pas remplace.
          if (panier && panier.classList.contains('is-empty')) {
            panier.classList.remove('is-empty');
          }
        }
      }
    }
  );
}